import { memo } from 'react';
import { toaster } from '@optiaxiom/react';
import { IconMessages } from '@optimizely/axiom-icons';
import { NodeProps } from '@xyflow/react';

import { extractApiErrorMessage } from '@frontend/api/http';

import {
  createMessageTrigger,
  deleteMessageTrigger,
  getMessageTrigger,
  updateMessageTrigger,
} from '../../services/message-trigger';
import { NodeConnectionTypes, NodeTypeCategory } from '../../types/connections';
import { NODE_TYPE } from '../../types/node';
import { defineNode } from '../../utils/node-factory';
import { BaseNodeRenderer } from '../components/renderers/BaseNodeRenderer/BaseNodeRenderer';

/**
 * Configuration for the Message Trigger node type.
 * Triggers workflow execution from incoming messages.
 */
export const messageTriggerConfig = defineNode({
  description: {
    displayName: 'Message Trigger',
    name: NODE_TYPE.MESSAGE,
    description: 'Trigger workflow execution from incoming messages',
    iconInfo: {
      icon: IconMessages,
      color: 'fg.success',
    },
    category: NodeTypeCategory.TRIGGER,
    input: {
      type: NodeConnectionTypes.Main,
      filter: undefined,
    },
    output: {
      type: NodeConnectionTypes.Main,
      displayName: 'Output',
      maxConnections: 1,
    },
    properties: [
      {
        displayName: 'Name',
        name: 'name',
        type: 'string',
        required: true,
        default: '',
        description: 'A descriptive name for this message trigger',
      },
      {
        displayName: 'Description',
        name: 'description',
        type: 'string',
        required: false,
        default: '',
        description: 'Optional description of what messages trigger this workflow',
      },
    ],
    defaults: {
      name: 'Message Trigger',
      parameters: {
        name: '',
        description: '',
      },
    },
  },

  enableAsyncGet: (_nodeId: string, nodeData?: Record<string, unknown>) => {
    const links =
      nodeData && typeof nodeData === 'object' ? (nodeData['links'] as Record<string, string>) : {};
    return Boolean(links && links['self'] && links['self'].trim().length > 0);
  },

  onAsyncSave: async (
    _nodeId: string,
    data: Record<string, unknown>,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    console.warn('context.nodeData', context.nodeData);
    if (!context.nodeData || !('links' in context.nodeData)) {
      try {
        const messageTrigger = await createMessageTrigger(
          context.workflowAgentId,
          context.instanceId,
          data
        );
        return messageTrigger;
      } catch (error) {
        const errorMessage = extractApiErrorMessage(error);
        throw new Error(errorMessage);
      }
    }

    const url = (context.nodeData['links'] as Record<string, string>)?.self;
    if (!url) {
      throw new Error('URL not found in node data');
    }

    try {
      await updateMessageTrigger(url, data);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error);
      throw new Error(errorMessage);
    }
  },

  onAsyncGet: async (
    _nodeId: string,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    if (!context.nodeData || !('links' in context.nodeData)) {
      throw new Error('Node data not found in the context');
    }

    const url = (context.nodeData['links'] as Record<string, string>)?.self;
    if (!url) {
      throw new Error('URL not found in node data');
    }

    try {
      const messageTrigger = await getMessageTrigger(url);
      return messageTrigger;
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return {};
      }

      console.error('Failed to fetch message trigger:', error);
      toaster.create('Failed to load message trigger configuration', { intent: 'danger' });
      throw error;
    }
  },

  onAsyncDelete: async (
    _nodeId: string,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    if (!context.nodeData || !('links' in context.nodeData)) {
      throw new Error('Node data not found in the context');
    }

    const url = (context.nodeData['links'] as Record<string, string>)?.self;
    if (!url) {
      throw new Error('URL not found in node data');
    }

    try {
      await deleteMessageTrigger(url);
    } catch (error) {
      console.error('Failed to delete message trigger:', error);
      const errorMessage = extractApiErrorMessage(error);
      throw new Error(errorMessage);
    }
  },

  render: memo(function MessageTriggerRenderer(props: NodeProps) {
    return <BaseNodeRenderer {...props} />;
  }),
});
